import React, { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";

import { verificarCredenciales } from "../services/seguridadService";

/**
 * Pide el usuario y la clave de quien aprueba una accion sensible y entrega el
 * token de autorizacion, que vale tres minutos y solo para esa accion.
 *
 * El usuario se escribe en blanco a proposito: la accion la aprueba el usuario
 * que tenga el acceso, que puede ser distinto del que esta trabajando en el
 * sistema.
 *
 * @param {boolean} abierto Muestra u oculta el dialogo.
 * @param {string} accion Codigo de la accion que se va a autorizar, por ejemplo
 *                       "desconsolidar". Viaja en el token.
 * @param {string} acceso Codigo del acceso que tiene que tener quien aprueba,
 *                      por ejemplo "pago.desconsolidar".
 * @param {string} titulo Titulo del dialogo.
 * @param {string} mensaje Texto que explica que se va a aprobar.
 * @param {function} onCancelar Se llama al cancelar.
 * @param {function} onConfirmar Se llama con el token cuando las credenciales
 *                              son correctas.
 */
const DialogoCredenciales = ({ abierto, accion, acceso, titulo, mensaje, onCancelar, onConfirmar }) => {
    const [user, setUser] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [verificando, setVerificando] = useState(false);

    const confirmar = async () => {
        setVerificando(true);
        setError("");

        try {
            const data = await verificarCredenciales(user.trim(), password, accion, acceso);

            setPassword("");
            onConfirmar(data);
        } catch (err) {
            console.log("Err: ", err);

            // 403 es que las credenciales son correctas pero el usuario no tiene
            // el acceso para aprobar esta accion.
            setError(err.response && err.response.status == 403
                ? "El usuario no tiene el acceso para aprobar esta accion."
                : "El usuario o la clave no son correctos.");
        } finally {
            setVerificando(false);
        }
    }

    const cerrar = () => {
        setUser("");
        setPassword("");
        setError("");
        onCancelar();
    }

    return (
        <Dialog open={abierto} fullWidth maxWidth={"xs"} onClose={cerrar}>
            <DialogTitle>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {titulo || "Aprobar con credenciales"}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={1} alignItems={"center"} sx={{ paddingTop: "12px" }}>
                    <Grid item xs={12} sm={12}>
                        <TextField
                            autoFocus
                            name="usuario"
                            label="Usuario que aprueba"
                            value={user}
                            fullWidth
                            onChange={(event) => setUser(event.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <TextField
                            name="password"
                            label="Clave"
                            type="password"
                            value={password}
                            fullWidth
                            onChange={(event) => setPassword(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    confirmar();
                                }
                            }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    {mensaje && (
                        <Grid item xs={12} sm={12}>
                            <Alert severity={"info"}>{mensaje}</Alert>
                        </Grid>
                    )}
                    {error != "" && (
                        <Grid item xs={12} sm={12}>
                            <Alert severity={"error"}>{error}</Alert>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant={"outlined"} onClick={cerrar}>{"Cancelar"}</Button>
                <Button
                    variant={"contained"}
                    disabled={verificando || user.trim() == "" || password == ""}
                    onClick={confirmar}
                >
                    {"Aprobar"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default DialogoCredenciales;
